import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { EditableFile } from "../models/EditableFile";
import EditableFileListItem from "./EditableFileListItem";
import { Button } from "../design/button";
import { useFile } from "../contexts/FileContext";
import { Task } from "../models/Task";
import { Subtask } from "../models/Subtask";
import { File } from "../models/File";
import { ChildSupportPayment } from "../models/ChildSupportPayment";
import { DiscoveryRequest } from "../models/DiscoveryRequest";
import { SpousalSupportPayment } from "../models/SpousalSupportPayment";
import { FinancialItem } from "../models/FinancialItem";
import { Participant } from "../models/Participant";
import { Event } from "../models/Event";

interface UploadFilesDialogProps {
  type: File["type"];
  event?: Event["_id"];
  participant?: Participant["_id"];
  discoveryRequest?: DiscoveryRequest["_id"];
  childSupportPayment?: ChildSupportPayment["_id"];
  spousalSupportPayment?: SpousalSupportPayment["_id"];
  financialItem?: FinancialItem["_id"];
  task?: Task["_id"];
  subtask?: Subtask["_id"];
  files: EditableFile[];
  setFiles: React.Dispatch<React.SetStateAction<EditableFile[]>>;
  isOpen: boolean;
  onClose: () => void;
  onFilesUploaded?: () => void;
}

const UploadFilesDialog: React.FC<UploadFilesDialogProps> = ({
  type,
  isOpen,
  onClose,
  files,
  setFiles,
  discoveryRequest,
  task,
  subtask,
  childSupportPayment,
  spousalSupportPayment,
  financialItem,
  event,
  participant,
  onFilesUploaded,
}) => {
  const { createFile } = useFile();
  const [isUploading, setIsUploading] = useState(false);

  const removeFile = (index: number) => {
    const fileToRemove = files[index];
    if (fileToRemove.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const updateFileName = (index: number, newName: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((file, i) =>
        i === index ? { ...file, name: newName } : file,
      ),
    );
  };

  const onUploadButtonPressed = async () => {
    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await createFile(file.file, {
          name: file.name,
          size: file.file.size,
          type,
          mimeType: file.file.type,
          description: file.description,
          status: "uploading",
          task,
          subtask,
          childSupportPayment,
          discoveryRequest,
          spousalSupportPayment,
          financialItem,
          participant,
          event,
        });
        removeFile(0);
      } catch (error) {
        console.error("Error uploading file:", error);
        alert("Failed to upload file. Please try again later.");
      }
    }

    setIsUploading(false);

    // ✅ Call the callback if provided
    if (onFilesUploaded) {
      onFilesUploaded();
    }

    // Optionally close dialog
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle>Upload Files</DialogTitle>
        <DialogBody>
          {files.map((fileObj, index) => (
            <EditableFileListItem
              key={index}
              editableFile={fileObj}
              onChangeFileName={(e) => updateFileName(index, e.target.value)}
              onRemove={() => removeFile(index)}
            />
          ))}
        </DialogBody>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={onUploadButtonPressed} disabled={isUploading}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadFilesDialog;
